import { Badge, Button } from '@auctisync/ui';
import { fetchEdgeFunction } from '@auctisync/api-client';
import { dollarsToCents } from '@auctisync/shared';
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';

/**
 * AI Complete review modal.
 *
 * Calls the `complete-item-from-photos` Edge Function, waits for the
 * structured response, then renders every field side-by-side with a
 * color-coded confidence badge and a checkbox. Staff selects which
 * suggestions to apply and clicks Apply Selected. The selected values
 * are handed back to the parent's applyToForm callback; the parent
 * decides how to merge them into form state.
 */

export type AiCompleteApplyPayload = {
  // Main inventory_items fields (written directly to the form).
  title?: string;
  description?: string;
  condition?: string;
  category_slug?: string; // caller resolves to category_id
  mpn?: string;
  upc?: string;
  msrp_cents?: number;
  retail_price_cents?: number;
  starting_bid_cents?: number;
  reserve_cents?: number;
  estimate_min_cents?: number;
  estimate_max_cents?: number;
  // Specs go into inventory_items.custom_fields.
  customFields: Record<string, string | number>;
};

type EdgeResponse = {
  values?: Record<string, unknown>;
  confidence?: Record<string, number>;
  pricing_logic?: string;
  identification_notes?: string;
  source_urls?: string[];
  searches_used?: number;
  available_categories?: Array<{ slug: string; name: string; id: string }>;
  error?: string;
  detail?: string;
};

type FieldRow = {
  key: string; // tenant-settings key (e.g. 'msrp')
  label: string;
  group: 'identity' | 'specs' | 'pricing';
  valueKey: string; // edge-response values[key]
  confidenceKey: string; // edge-response confidence[key]
  format: (v: unknown) => string;
  applyTo: 'field' | 'custom_fields';
  inputKind: 'text' | 'textarea' | 'number' | 'dollars';
};

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const strOrNum = (v: unknown): string =>
  typeof v === 'number' ? String(v) : typeof v === 'string' ? v : '';
const centsFmt = (v: unknown): string => (typeof v === 'number' ? (v / 100).toFixed(2) : '');

const FIELD_ROWS: FieldRow[] = [
  {
    key: 'title',
    label: 'Title',
    group: 'identity',
    valueKey: 'title',
    confidenceKey: 'title',
    format: str,
    applyTo: 'field',
    inputKind: 'text',
  },
  {
    key: 'description',
    label: 'Description',
    group: 'identity',
    valueKey: 'description',
    confidenceKey: 'description',
    format: str,
    applyTo: 'field',
    inputKind: 'textarea',
  },
  {
    key: 'condition',
    label: 'Condition',
    group: 'identity',
    valueKey: 'condition',
    confidenceKey: 'condition',
    format: str,
    applyTo: 'field',
    inputKind: 'text',
  },
  {
    key: 'category',
    label: 'Category',
    group: 'identity',
    valueKey: 'category_slug',
    confidenceKey: 'category_slug',
    format: str,
    applyTo: 'field',
    inputKind: 'text',
  },
  {
    key: 'mpn',
    label: 'MPN',
    group: 'identity',
    valueKey: 'mpn',
    confidenceKey: 'mpn',
    format: str,
    applyTo: 'field',
    inputKind: 'text',
  },
  {
    key: 'upc',
    label: 'UPC',
    group: 'identity',
    valueKey: 'upc',
    confidenceKey: 'upc',
    format: str,
    applyTo: 'field',
    inputKind: 'text',
  },
  {
    key: 'brand',
    label: 'Brand',
    group: 'specs',
    valueKey: 'brand',
    confidenceKey: 'brand',
    format: str,
    applyTo: 'custom_fields',
    inputKind: 'text',
  },
  {
    key: 'model',
    label: 'Model',
    group: 'specs',
    valueKey: 'model',
    confidenceKey: 'model',
    format: str,
    applyTo: 'custom_fields',
    inputKind: 'text',
  },
  {
    key: 'year',
    label: 'Year',
    group: 'specs',
    valueKey: 'year',
    confidenceKey: 'year',
    format: strOrNum,
    applyTo: 'custom_fields',
    inputKind: 'number',
  },
  {
    key: 'material',
    label: 'Material',
    group: 'specs',
    valueKey: 'material',
    confidenceKey: 'material',
    format: str,
    applyTo: 'custom_fields',
    inputKind: 'text',
  },
  {
    key: 'color',
    label: 'Color',
    group: 'specs',
    valueKey: 'color',
    confidenceKey: 'color',
    format: str,
    applyTo: 'custom_fields',
    inputKind: 'text',
  },
  {
    key: 'dimensions',
    label: 'Dimensions',
    group: 'specs',
    valueKey: 'dimensions',
    confidenceKey: 'dimensions',
    format: str,
    applyTo: 'custom_fields',
    inputKind: 'text',
  },
  {
    key: 'msrp',
    label: 'MSRP',
    group: 'pricing',
    valueKey: 'msrp_cents',
    confidenceKey: 'msrp_cents',
    format: centsFmt,
    applyTo: 'field',
    inputKind: 'dollars',
  },
  {
    key: 'retail_price',
    label: 'Retail price',
    group: 'pricing',
    valueKey: 'retail_price_cents',
    confidenceKey: 'retail_price_cents',
    format: centsFmt,
    applyTo: 'field',
    inputKind: 'dollars',
  },
  {
    key: 'starting_bid',
    label: 'Starting bid',
    group: 'pricing',
    valueKey: 'starting_bid_cents',
    confidenceKey: 'starting_bid_cents',
    format: centsFmt,
    applyTo: 'field',
    inputKind: 'dollars',
  },
  {
    key: 'reserve',
    label: 'Reserve',
    group: 'pricing',
    valueKey: 'reserve_cents',
    confidenceKey: 'reserve_cents',
    format: centsFmt,
    applyTo: 'field',
    inputKind: 'dollars',
  },
  {
    key: 'estimate_min',
    label: 'Estimate (low)',
    group: 'pricing',
    valueKey: 'estimate_min_cents',
    confidenceKey: 'estimate_min_cents',
    format: centsFmt,
    applyTo: 'field',
    inputKind: 'dollars',
  },
  {
    key: 'estimate_max',
    label: 'Estimate (high)',
    group: 'pricing',
    valueKey: 'estimate_max_cents',
    confidenceKey: 'estimate_max_cents',
    format: centsFmt,
    applyTo: 'field',
    inputKind: 'dollars',
  },
];

function confidenceColor(pct: number): { bg: string; fg: string; border: string; label: string } {
  if (pct >= 80) return { bg: '#dcfce7', fg: '#166534', border: '#86efac', label: `${pct}%` };
  if (pct >= 50) return { bg: '#fef9c3', fg: '#854d0e', border: '#fde047', label: `${pct}%` };
  return { bg: '#fee2e2', fg: '#991b1b', border: '#fecaca', label: `${pct}%` };
}

export function AiCompleteModal({
  inventoryItemId,
  lotId,
  enabledFields,
  currentItem,
  onApply,
  onClose,
}: {
  /** Item mode — pass this on the inventory page. */
  inventoryItemId?: string;
  /** Lot mode — pass this on the lot page. If both are passed, lot wins. */
  lotId?: string;
  enabledFields: Record<string, boolean>;
  currentItem: {
    title: string | null;
    description: string | null;
    condition: string | null;
    category_id: string | null;
    mpn: string | null;
    upc: string | null;
    msrp_cents: number | null;
    retail_price_cents: number | null;
    starting_bid_cents: number | null;
    reserve_cents: number | null;
    estimate_min_cents: number | null;
    estimate_max_cents: number | null;
    custom_fields: Record<string, unknown> | null;
  };
  onApply: (payload: AiCompleteApplyPayload) => void;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resp, setResp] = useState<EdgeResponse | null>(null);
  const [checks, setChecks] = useState<Record<string, boolean>>({});
  // Edited values — strings for input binding, converted at apply-time.
  const [edits, setEdits] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        // Direct (not `functions.invoke`) so the error body stays readable;
        // the token and the one-refresh retry on a 401 come from the shared client.
        const res = await fetchEdgeFunction(supabase, 'complete-item-from-photos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(lotId ? { lot_id: lotId } : { inventory_item_id: inventoryItemId }),
        });
        const raw = await res.text();
        let data: EdgeResponse = {};
        try {
          data = JSON.parse(raw) as EdgeResponse;
        } catch {
          data = { error: raw.slice(0, 500) };
        }
        if (cancelled) return;
        if (!res.ok || !data.values) {
          setError(
            [
              `HTTP ${res.status}`,
              data.error,
              data.detail ? `detail: ${data.detail.slice(0, 300)}` : null,
            ]
              .filter(Boolean)
              .join(' · '),
          );
          setLoading(false);
          return;
        }
        setResp(data);
        // Default-check each enabled field if: (a) AI returned a non-null
        // value AND (b) either the current field is empty OR confidence >= 70.
        // Also seed the editable `edits` map with the formatted AI value.
        const initialChecks: Record<string, boolean> = {};
        const initialEdits: Record<string, string> = {};
        for (const row of FIELD_ROWS) {
          if (!enabledFields[row.key]) continue;
          const v = data.values?.[row.valueKey];
          initialEdits[row.key] = row.format(v);
          if (v === null || v === undefined || v === '') continue;
          const conf = data.confidence?.[row.confidenceKey] ?? 0;
          const currentVal = readCurrent(row, currentItem);
          const isEmpty = currentVal === null || currentVal === undefined || currentVal === '';
          initialChecks[row.key] = isEmpty || conf >= 70;
        }
        setChecks(initialChecks);
        setEdits(initialEdits);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inventoryItemId, enabledFields, currentItem]);

  const rows = useMemo(() => FIELD_ROWS.filter((r) => enabledFields[r.key]), [enabledFields]);

  function toggle(key: string, next: boolean) {
    setChecks((prev) => ({ ...prev, [key]: next }));
  }

  function setEdit(key: string, next: string) {
    setEdits((prev) => ({ ...prev, [key]: next }));
  }

  function applySelected() {
    const payload: AiCompleteApplyPayload = { customFields: {} };
    for (const row of rows) {
      if (!checks[row.key]) continue;
      const raw = (edits[row.key] ?? '').trim();
      if (raw === '') continue;
      if (row.applyTo === 'custom_fields') {
        if (row.inputKind === 'number') {
          const n = parseInt(raw, 10);
          if (Number.isFinite(n)) payload.customFields[row.key] = n;
        } else {
          payload.customFields[row.key] = raw;
        }
      } else {
        switch (row.key) {
          case 'title':
          case 'description':
          case 'condition':
          case 'mpn':
          case 'upc':
            payload[row.key] = raw;
            break;
          case 'category':
            payload.category_slug = raw;
            break;
          case 'msrp':
          case 'retail_price':
          case 'starting_bid':
          case 'reserve':
          case 'estimate_min':
          case 'estimate_max': {
            const dollars = parseFloat(raw);
            if (!Number.isFinite(dollars) || dollars < 0) break;
            const cents = dollarsToCents(dollars);
            if (row.key === 'msrp') payload.msrp_cents = cents;
            else if (row.key === 'retail_price') payload.retail_price_cents = cents;
            else if (row.key === 'starting_bid') payload.starting_bid_cents = cents;
            else if (row.key === 'reserve') payload.reserve_cents = cents;
            else if (row.key === 'estimate_min') payload.estimate_min_cents = cents;
            else if (row.key === 'estimate_max') payload.estimate_max_cents = cents;
            break;
          }
        }
      }
    }
    onApply(payload);
  }

  return (
    <div style={backdropStyle} role="dialog" aria-modal="true" aria-label="AI Complete">
      <div style={modalStyle}>
        <header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '1rem',
            flexWrap: 'wrap',
            marginBottom: '0.75rem',
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>AI Complete</h2>
            <p style={{ margin: '0.15rem 0 0', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              Review the draft, uncheck anything you want to keep as-is, then apply.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '1.4rem',
              cursor: 'pointer',
              color: 'var(--text-muted)',
            }}
          >
            ×
          </button>
        </header>

        {loading && <LoadingView />}

        {error && (
          <div role="alert" style={errStyle}>
            {error}
          </div>
        )}

        {resp && !loading && !error && (
          <>
            {(resp.pricing_logic || resp.identification_notes) && (
              <section style={insightCardStyle}>
                {resp.identification_notes && (
                  <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text)' }}>
                    <strong>Identification:</strong> {resp.identification_notes}
                  </p>
                )}
                {resp.pricing_logic && (
                  <p style={{ margin: '0.3rem 0 0', fontSize: '0.85rem', color: 'var(--text)' }}>
                    <strong>Pricing logic:</strong> {resp.pricing_logic}
                  </p>
                )}
                {resp.source_urls && resp.source_urls.length > 0 && (
                  <p
                    style={{
                      margin: '0.4rem 0 0',
                      fontSize: '0.75rem',
                      color: 'var(--text-muted)',
                      wordBreak: 'break-all',
                    }}
                  >
                    Sources:{' '}
                    {resp.source_urls.map((u, i) => (
                      <span key={u}>
                        <a
                          href={u}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: 'var(--brand-primary)' }}
                        >
                          {hostname(u)}
                        </a>
                        {i < (resp.source_urls?.length ?? 0) - 1 ? ' · ' : ''}
                      </span>
                    ))}
                  </p>
                )}
              </section>
            )}

            <FieldGroup
              title="Identity"
              rows={rows.filter((r) => r.group === 'identity')}
              resp={resp}
              currentItem={currentItem}
              checks={checks}
              onToggle={toggle}
              edits={edits}
              onEdit={setEdit}
            />
            <FieldGroup
              title="Specs"
              rows={rows.filter((r) => r.group === 'specs')}
              resp={resp}
              currentItem={currentItem}
              checks={checks}
              onToggle={toggle}
              edits={edits}
              onEdit={setEdit}
            />
            <FieldGroup
              title="Pricing"
              rows={rows.filter((r) => r.group === 'pricing')}
              resp={resp}
              currentItem={currentItem}
              checks={checks}
              onToggle={toggle}
              edits={edits}
              onEdit={setEdit}
            />

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '0.5rem',
                marginTop: '0.75rem',
              }}
            >
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="button" onClick={applySelected}>
                Apply selected
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function FieldGroup({
  title,
  rows,
  resp,
  currentItem,
  checks,
  onToggle,
  edits,
  onEdit,
}: {
  title: string;
  rows: FieldRow[];
  resp: EdgeResponse;
  currentItem: {
    title: string | null;
    description: string | null;
    condition: string | null;
    category_id: string | null;
    mpn: string | null;
    upc: string | null;
    msrp_cents: number | null;
    retail_price_cents: number | null;
    starting_bid_cents: number | null;
    reserve_cents: number | null;
    estimate_min_cents: number | null;
    estimate_max_cents: number | null;
    custom_fields: Record<string, unknown> | null;
  };
  checks: Record<string, boolean>;
  onToggle: (key: string, next: boolean) => void;
  edits: Record<string, string>;
  onEdit: (key: string, next: string) => void;
}) {
  if (rows.length === 0) return null;
  return (
    <section style={groupStyle}>
      <h3 style={groupHeadingStyle}>{title}</h3>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr>
              <th style={thStyle}></th>
              <th style={thStyle}>Field</th>
              <th style={thStyle}>Current</th>
              <th style={thStyle}>AI draft · editable</th>
              <th style={thStyle}>Confidence</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const aiSuggestion = row.format(resp.values?.[row.valueKey]);
              const current = row.format(readCurrent(row, currentItem));
              const confNum = resp.confidence?.[row.confidenceKey] ?? 0;
              const color = confidenceColor(confNum);
              const hasAiValue = aiSuggestion.length > 0;
              const edited = edits[row.key] ?? '';
              return (
                <tr key={row.key} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ ...tdStyle, width: '2rem' }}>
                    <input
                      type="checkbox"
                      checked={Boolean(checks[row.key])}
                      onChange={(e) => onToggle(row.key, e.target.checked)}
                    />
                  </td>
                  <td style={{ ...tdStyle, width: '9rem', color: 'var(--text)' }}>{row.label}</td>
                  <td style={{ ...tdStyle, color: 'var(--text-muted)', maxWidth: '10rem' }}>
                    {current || <span style={{ fontStyle: 'italic' }}>empty</span>}
                  </td>
                  <td style={{ ...tdStyle, maxWidth: '20rem' }}>
                    <EditableCell row={row} value={edited} onChange={(v) => onEdit(row.key, v)} />
                  </td>
                  <td style={{ ...tdStyle, width: '5.5rem' }}>
                    {hasAiValue ? (
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '0.15rem 0.5rem',
                          borderRadius: '999px',
                          background: color.bg,
                          color: color.fg,
                          border: `1px solid ${color.border}`,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {color.label}
                      </span>
                    ) : (
                      <Badge variant="outline">n/a</Badge>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function EditableCell({
  row,
  value,
  onChange,
}: {
  row: FieldRow;
  value: string;
  onChange: (v: string) => void;
}) {
  if (row.inputKind === 'textarea') {
    return (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={5}
        style={{ ...editInputStyle, minHeight: '5rem', resize: 'vertical', fontFamily: 'inherit' }}
      />
    );
  }
  if (row.inputKind === 'dollars') {
    return (
      <div style={{ position: 'relative' }}>
        <span
          style={{
            position: 'absolute',
            left: '0.6rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--text-muted)',
            fontSize: '0.85rem',
            pointerEvents: 'none',
          }}
        >
          $
        </span>
        <input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ ...editInputStyle, paddingLeft: '1.4rem' }}
          placeholder="0.00"
        />
      </div>
    );
  }
  if (row.inputKind === 'number') {
    return (
      <input
        type="text"
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={editInputStyle}
      />
    );
  }
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={editInputStyle}
    />
  );
}

const editInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.35rem 0.55rem',
  border: '1px solid var(--border)',
  borderRadius: '0.3rem',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.85rem',
};

function readCurrent(
  row: FieldRow,
  item: {
    title: string | null;
    description: string | null;
    condition: string | null;
    category_id: string | null;
    mpn: string | null;
    upc: string | null;
    msrp_cents: number | null;
    retail_price_cents: number | null;
    starting_bid_cents: number | null;
    reserve_cents: number | null;
    estimate_min_cents: number | null;
    estimate_max_cents: number | null;
    custom_fields: Record<string, unknown> | null;
  },
): unknown {
  if (row.applyTo === 'custom_fields') {
    return item.custom_fields?.[row.key] ?? null;
  }
  switch (row.key) {
    case 'title':
      return item.title;
    case 'description':
      return item.description;
    case 'condition':
      return item.condition;
    case 'category':
      return item.category_id; // we show the id; not ideal but acceptable for v1
    case 'mpn':
      return item.mpn;
    case 'upc':
      return item.upc;
    case 'msrp':
      return item.msrp_cents;
    case 'retail_price':
      return item.retail_price_cents;
    case 'starting_bid':
      return item.starting_bid_cents;
    case 'reserve':
      return item.reserve_cents;
    case 'estimate_min':
      return item.estimate_min_cents;
    case 'estimate_max':
      return item.estimate_max_cents;
    default:
      return null;
  }
}

function hostname(u: string): string {
  try {
    return new URL(u).hostname.replace(/^www\./, '');
  } catch {
    return u;
  }
}

function LoadingView() {
  return (
    <div
      style={{
        padding: '2rem 1rem',
        textAlign: 'center',
        color: 'var(--text-muted)',
        fontSize: '0.9rem',
      }}
    >
      <div style={{ marginBottom: '0.5rem', fontSize: '1.5rem' }}>⚡</div>
      AuctiSync AI is inspecting your photos, researching pricing, and drafting every field. This
      usually takes 10–20 seconds.
    </div>
  );
}

const backdropStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.55)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '3rem 1rem 1rem',
  zIndex: 100,
  overflowY: 'auto',
};

const modalStyle: React.CSSProperties = {
  background: 'var(--surface)',
  borderRadius: '0.5rem',
  padding: '1.25rem 1.5rem',
  maxWidth: '56rem',
  width: '100%',
  boxShadow: '0 20px 40px rgba(15, 23, 42, 0.2)',
};

const errStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  borderRadius: '0.375rem',
  background: 'var(--surface-danger, #fef2f2)',
  color: 'var(--text-danger, #991b1b)',
  border: '1px solid var(--border-danger, #fecaca)',
  fontSize: '0.85rem',
};

const insightCardStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  background: 'var(--surface-muted, #f8fafc)',
  border: '1px dashed var(--border)',
  borderRadius: '0.5rem',
  marginBottom: '0.75rem',
};

const groupStyle: React.CSSProperties = {
  marginBottom: '0.75rem',
  border: '1px solid var(--border)',
  borderRadius: '0.5rem',
  overflow: 'hidden',
};

const groupHeadingStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--text-muted)',
  margin: 0,
  padding: '0.5rem 0.75rem',
  background: 'var(--surface-elev)',
  borderBottom: '1px solid var(--border)',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.4rem 0.6rem',
  fontSize: '0.7rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--text-muted)',
  background: 'var(--surface)',
};

const tdStyle: React.CSSProperties = {
  padding: '0.45rem 0.6rem',
  verticalAlign: 'top',
};
